import {useMutation, useQuery} from '@apollo/client';
import {Button} from '@dabafinance/react-components';
import {Form, Select, Spin} from 'antd';
import FounderCreation from 'components/Startup/creation/founder';
import {
  ADD_INDUSTRY_TO_COMPANY,
  ADD_TOPIC_TO_COMPANY,
} from 'graphql/mutations/investorUpdates';
import {
  GET_ALL_INDUSTRIES,
  GET_ALL_TOPICS,
} from 'graphql/queries/investorUpdates';
import {useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import styles from './categories.module.scss';
import CompanySocials from './CompanySocials';
import CompanyStock from 'components/CompanyStock/CompanyStock';
import CompanyWizardHeader from 'components/Startup/creation/wizard/CompanyWizardHeader';

const CompanyCategories = () => {
  const [topics, setTopics] = useState([]);
  const {id} = useParams();
  const navigate = useNavigate();
  const [industries, setIndustries] = useState('');
  const [activePage, setActivePage] = useState('CATEGORIES');
  const [errors, setErrors] = useState({});
  const {Option} = Select;

  const handleBack = () => {
    if (activePage === 'STOCKS') setActivePage('CATEGORIES');
    else if (activePage === 'SOCIALS') setActivePage('STOCKS');
    else if (activePage === 'MEMBERS') setActivePage('SOCIALS');
    else navigate('/dashboard/opportunities/startups/');
  };

  const handleStepClick = key => {
    if (['CATEGORIES', 'STOCKS', 'SOCIALS'].includes(key)) setActivePage(key);
  };

  // Step 2 "Continue" now persists topics AND industry before advancing, so
  // admins no longer silently lose their selections by skipping the old
  // separate "Save" buttons.
  const handleCategoriesContinue = async () => {
    const nextErrors = {};
    if (!topics.length) nextErrors.topics = 'Select at least one topic';
    if (!industries) nextErrors.industry = 'Select an industry';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    try {
      const topicRes = await addTopicsToCompany({
        variables: {companyId: id, topicIds: topics.map(d => d.split(';')[1])},
      });
      if (topicRes?.data?.addTopicsToCompany?.__typename === 'Error') {
        return toast.error(topicRes.data.addTopicsToCompany.message);
      }
      const industryRes = await addIndustryToCompany({
        variables: {companyId: id, industryId: industries.split(';')[1]},
      });
      if (industryRes?.data?.addIndustryToCompany?.__typename === 'Error') {
        return toast.error(industryRes.data.addIndustryToCompany.message);
      }
      toast.success('Categories saved');
      setActivePage('STOCKS');
    } catch (error) {
      toast.error(error?.message || 'Something went wrong saving categories');
    }
  };

  const {data: getAllTopics, loading} = useQuery(GET_ALL_TOPICS);
  const {data: getAllIndustries} = useQuery(GET_ALL_INDUSTRIES);

  const [addTopicsToCompany, {loading: updatingTopics}] =
    useMutation(ADD_TOPIC_TO_COMPANY);
  const [addIndustryToCompany, {loading: updatingIndustry}] = useMutation(
    ADD_INDUSTRY_TO_COMPANY,
  );

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
        <h1>Loading...</h1>
      </div>
    );
  }
  return (
    <div>
      <div className={styles['stepper-wrap']}>
        <CompanyWizardHeader
          currentStepKey={activePage}
          onBack={handleBack}
          onStepClick={handleStepClick}
        />
      </div>
      {activePage === 'CATEGORIES' && (
        <div className={styles.container}>
          <h2 className={styles['section-title']}>
            Select company topics and industries
          </h2>
          <p className={styles['section-hint']}>
            These help investors discover the company. Both are required.
          </p>
          <Form className={styles.form} layout="vertical">
            <Form.Item label="Topics" name="Topics" required>
              <Select
                mode="multiple"
                showSearch
                value={topics}
                onChange={e => {
                  setTopics(e);
                  setErrors(prev => ({...prev, topics: undefined}));
                }}
                style={{width: 730}}
                placeholder={'Select Company Topics'}>
                {getAllTopics?.getAllTopics?.map(topic => (
                  <Option key={`${topic?.name};${topic?.id}`}>
                    <div className={styles['all-data']}>
                      <img src={topic.icon} alt="" />
                      <span>{topic.name}</span>
                    </div>
                  </Option>
                ))}
              </Select>
              {errors.topics && (
                <span className={styles['field-error']}>{errors.topics}</span>
              )}
            </Form.Item>
            <Form.Item
              label="Industry"
              className={styles['form-item']}
              name="industry"
              required>
              <Select
                className={styles['select-field']}
                showSearch
                value={industries}
                onChange={value => {
                  setIndustries(value);
                  setErrors(prev => ({...prev, industry: undefined}));
                }}
                style={{width: 730}}
                placeholder={'Select Industries'}>
                {getAllIndustries?.getAllIndustries?.map(industry => (
                  <Option key={`${industry.name};${industry.id}`}>
                    <div className={styles['all-data']}>
                      <img src={industry.icon} alt="" />
                      <span>{industry.name}</span>
                    </div>
                  </Option>
                ))}
              </Select>
              {errors.industry && (
                <span className={styles['field-error']}>{errors.industry}</span>
              )}
            </Form.Item>
          </Form>
          <div className={styles.footer}>
            <Button
              className={styles['footer-button-style']}
              label="Continue"
              disabled={updatingTopics || updatingIndustry}
              onClick={handleCategoriesContinue}
            />
          </div>
        </div>
      )}
      {activePage === 'SOCIALS' && (
        <CompanySocials setActivePage={setActivePage} />
      )}
      {activePage === 'MEMBERS' && (
        <FounderCreation setActivePage={setActivePage} />
      )}
      {activePage === 'STOCKS' && (
        <CompanyStock setActivePage={setActivePage} />
      )}
    </div>
  );
};

export default CompanyCategories;
